import { NgModule}  from '@angular/core';
import {
    RouterModule,
    UrlHandlingStrategy,
    Router,
    Route,
    Routes,
    NavigationError
} from '@angular/router';
import { UpgradeModule } from '@angular/upgrade/static';
import { filter } from 'rxjs/operators';
import { EmptyComponent } from './components/empty/empty.component';
import { Ng1NgxUrlHandlingStrategyService } from './services/ng1-ngx-url-handling-strategy.service';
import { FarmnetModuleService } from './services/farmnet-module.service';
import { FarmnetModuleLoadingService } from './services/farmnet-module-loading.service';
import { ModuleConfig } from './models/module-config.interface';
import { TestServiceComponent } from '../components/test-service.component';
import { TestServiceGuard } from '../components/test-service.guard';
import { FNW_ROUTES } from '@farmnet/webapp-loader-companion';

/*
    rewritten setUpLocationSync from @angular/router/upgrade for hashBased routing which does not work for hash based routing
    https://github.com/angular/angular/issues/18832
 */
export function setUpLocationSync(ngUpgrade: UpgradeModule) {
    if (!ngUpgrade.$injector) {
        throw new Error(`
        RouterUpgradeInitializer can be used only after UpgradeModule.bootstrap has been called.
        Remove RouterUpgradeInitializer and call setUpLocationSync after UpgradeModule.bootstrap.
      `);
    }

    const router: Router = ngUpgrade.injector.get(Router);
    const url = document.createElement('a');

    ngUpgrade.$injector.get('$rootScope')
        .$on('$locationChangeStart', (_: any, next: string, __: string) => {
            if (url.href === next) { return; }
            url.href = next;
            router.navigateByUrl(url.hash.toString().replace(/#/g, ''));
        });
}

const defaultRoute: Route = {path: '**', component: EmptyComponent};
const testServiceRoute: Route = {
    path: 'test-service',
    component: TestServiceComponent,
    canDeactivate: [TestServiceGuard]
};

const ROUTES: Routes = [
    ...(FNW_ROUTES as Route[]),
    testServiceRoute,
    defaultRoute,
];

@NgModule({
    imports: [
        RouterModule.forRoot(ROUTES, {useHash: true, enableTracing: false})
    ],
    providers: [
        {provide: UrlHandlingStrategy, useExisting: Ng1NgxUrlHandlingStrategyService}
    ],
    exports: [
        RouterModule
    ]
})
export class CfnRoutingModule {
    constructor(
        private router: Router,
        private farmnetModuleService: FarmnetModuleService,
        private farmnetModuleLoadingService: FarmnetModuleLoadingService
    ) {
        const dynamicRoutes: Route[] = this.farmnetModuleService.getNgxModules().map(module => {
            return this.mapToRoute(module);
        });

        dynamicRoutes.forEach(route => {
            this.router.config.unshift(route);
        });

        this.farmnetModuleService.ngxModulesUpdate().subscribe(modules => {
            this.updateRoutes();
        });

        router.events
            .pipe(
                filter(event => event instanceof NavigationError)
            )
            .subscribe((event) => {
                console.log('route error', event);
                window.location.href = '#/error/pageNotFound';
            });
    }

    updateRoutes() {
        const addedModuleNames: string[] = [];

        this.router.config.forEach((route, index, routes) => {
            if (route.data && route.data.module) {
                const oldModule: ModuleConfig = route.data.module;
                const newModule = this.farmnetModuleService.findNgxModule(oldModule.name);

                if (newModule) {
                    // possibly update route
                    route.loadChildren = this.farmnetModuleLoadingService.loadModuleFactory(newModule);
                    addedModuleNames.push(newModule.name)
                } // no else always retry once registered routes
            }
        });

        const updatedModules = this.farmnetModuleService.getNgxModules();

        // add new routes
        const notYetAddedRoutes = updatedModules
            .filter(updatedModule => !addedModuleNames.includes(updatedModule.name))
            .map(module => {
                return this.mapToRoute(module);
            });

        notYetAddedRoutes.forEach(route => {
            this.router.config.unshift(route);
        });

    }

    private mapToRoute(module: ModuleConfig): Route {
        return {
            path: module.name,
            loadChildren: this.farmnetModuleLoadingService.loadModuleFactory(module),
            data: {
                module
            }
        };
    }
}
